/* WF-049 · the passive display of a screen support — v3.3 migration, pack supports-b.
   One support, on a screen: the establishment, the title, « Scannez pour continuer sur votre téléphone. », the support's
   REAL QR (supportUrl(token), drawn by @brand/qr), nothing to touch. No wheel, no prizes, no interactive mode, no review
   bar: the kiosk (routes/supports/Kiosque) is out of V1 and nothing of it carries over. Landscape or portrait follows the
   screen itself (the words beside the QR, or above it). « ÉPREUVE DE DÉMONSTRATION · aucun cadeau réel » shows only while
   the support opens no live campaign (readiness « destination »): once it does, the QR is the customers'. « Plein écran »
   asks the browser; « Retour à la préparation » goes back to the support's « Test et mise en place ». No merchant chrome:
   the route sits beside /kiosque/:code in App.tsx. */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiWrites, supportUrl, useCampaigns, useLocations, useSupports } from '@/lib/api'
import { allCampaigns, mergeSupports, servedBy, supportText, defaultSupportDesign, SUPPORT_LOCKED_LINE, type SupportDesign } from '@/lib/campaign'
import { qrMarkup } from './Designer'
import { templateColours } from './Templates'

export default function Affichage() {
  const { id } = useParams()
  const nav = useNavigate()
  const { data, isPending } = useSupports()
  const { data: locations } = useLocations()
  const { data: apiCampaigns } = useCampaigns()
  const [full, setFull] = useState(false)
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', on)
    return () => document.removeEventListener('fullscreenchange', on)
  }, [])
  if (isPending) return null
  const names: Record<string, string> = Object.fromEntries((locations ?? []).map(l => [l.id, l.name]))
  const lite = allCampaigns(apiCampaigns, names)
  const s = mergeSupports(data).find(x => x.id === id)
  if (!s) return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bmr-ground)', color: 'var(--bmr-text)', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", padding: 24, textAlign: 'center' }} data-testid="affichage-erreur">
      <div><p className="t-section">Ce support n’existe pas, ou plus.</p><button type="button" style={btn()} onClick={() => nav('/supports')}>Retour aux supports</button></div>
    </div>
  )
  const place = names[s.locationId] ?? 'l’établissement'
  const live = !!servedBy(s, lite).current
  const design: SupportDesign = s.design ?? defaultSupportDesign('ecran-paysage')
  const c = templateColours(design)
  const url = s.token ? supportUrl(s.token) : undefined
  const svg = url ? qrMarkup(url, `QR de « ${s.name} »`) : null
  const title = supportText(design, 'sup.title', place)
  const fullscreen = () => { const el = document.documentElement; if (document.fullscreenElement) void document.exitFullscreen?.(); else void el.requestFullscreen?.() }
  return (
    <div data-testid="affichage" style={{ minHeight: '100dvh', background: 'var(--bmr-ground)', color: 'var(--bmr-text)', display: 'grid', gridTemplateRows: '1fr auto', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <main style={{ margin: 'clamp(12px, 3vw, 40px)', borderTop: `8px solid ${c.accent}`, background: c.bg, color: c.text, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', alignItems: 'center', gap: 'clamp(20px, 5vw, 64px)', padding: 'clamp(24px, 6vw, 72px)' }}>
        <div style={{ display: 'grid', gap: 'clamp(12px, 2vw, 22px)', alignContent: 'center' }}>
          <p style={{ margin: 0, font: "400 clamp(18px, 2.2vw, 26px)/1.2 Lora, Georgia, serif", letterSpacing: '.18em', color: c.muted }}>{supportText(design, 'sup.name', place)}</p>
          <h1 style={{ margin: 0, font: "700 clamp(34px, 5.6vw, 72px)/1.08 Lora, Georgia, serif", color: c.accent, textWrap: 'balance' as never }}>{title}</h1>
          <p style={{ margin: 0, fontSize: 'clamp(18px, 2.2vw, 28px)', lineHeight: 1.35, color: c.text }}>Scannez pour continuer sur votre téléphone.</p>
          <p style={{ margin: 0, fontSize: 'clamp(12px, 1.2vw, 15px)', color: c.muted }}>{SUPPORT_LOCKED_LINE}</p>
          {!live && <p data-testid="affichage-epreuve" style={{ margin: 'clamp(12px, 3vw, 40px) 0 0', font: "600 11px/1.3 'Plus Jakarta Sans', sans-serif", letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted }}>Épreuve de démonstration · aucun cadeau réel</p>}
        </div>
        <div style={{ display: 'grid', justifyItems: 'center' }}>
          {svg
            ? <div data-testid="affichage-qr" style={{ width: 'min(100%, 46vh, 520px)', aspectRatio: '1', background: '#fff', padding: '4%', boxSizing: 'border-box', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: svg }} />
            : <p style={{ margin: 0, color: c.muted }}>Ce support n’a pas de lien&nbsp;: rien à afficher.</p>}
        </div>
      </main>
      {!full && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', padding: '0 16px 20px' }}>
          <button type="button" style={btn()} onClick={fullscreen}>Plein écran</button>
          <button type="button" style={btn()} onClick={() => nav(`/supports?vue=detail&id=${encodeURIComponent(s.id)}&onglet=test`)}>Retour à la préparation</button>
          <span style={{ fontSize: 12.5, color: 'var(--bmr-text-muted)' }}>{apiWrites ? 'Affichage du support · le QR est le vrai' : 'Affichage de démonstration · données locales'}</span>
        </div>
      )}
    </div>
  )
}

function btn(): React.CSSProperties {
  return { font: "600 13px 'Plus Jakarta Sans', sans-serif", borderRadius: 10, padding: '10px 14px', cursor: 'pointer', border: '1px solid var(--bmr-line)', background: 'var(--bmr-chalk)', color: 'var(--bmr-ink)' }
}
